/**
 * Device Filtering API Utility
 *
 * Provides a centralized interface for calling the backend hybrid filter endpoint
 * Handles pagination, authorization, error handling, and response normalization
 *
 * Usage:
 *   const { devices, pagination } = await fetchFilteredDevices(filters, pagination, token);
 */

import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';

/**
 * Fetch filtered devices from backend with pagination support
 *
 * @param {Object} filters - Filter state object with structure:
 *   {
 *     locations: [],
 *     departments: [],
 *     serviceContractTypes: [],
 *     complianceRequired: null,
 *     calibrationRequired: null,
 *     productCostFrom: null,
 *     productCostTo: null,
 *     ppmStatus: [],
 *     calibrationStatus: [],
 *     complianceStatus: [],
 *     coverageStatus: [],  // CONSOLIDATED: applies to both warranty and contract status
 *     hasActiveComplaints: false,
 *     hasNoActiveComplaints: false,
 *     installationDateFrom: null,
 *     installationDateTo: null,
 *     warrantyDateFrom: null,
 *     warrantyDateTo: null,
 *     contractDateFrom: null,
 *     contractDateTo: null,
 *     search: ""
 *   }
 *
 * @param {Object} pagination - Pagination parameters:
 *   {
 *     page: 1,
 *     per_page: 25
 *   }
 *
 * @param {string} token - JWT authentication token
 *
 * @param {Object} sorting - Optional sorting parameters:
 *   {
 *     sort_by: 'name',  // Column to sort by
 *     sort_direction: 'asc'  // 'asc' or 'desc'
 *   }
 *
 * @returns {Promise<Object>} Response object:
 *   {
 *     success: true,
 *     devices: [...],
 *     pagination: {
 *       page: 1,
 *       per_page: 25,
 *       total_count: 156,
 *       total_pages: 7,
 *       has_next: true,
 *       has_previous: false
 *     },
 *     summary: {
 *       total_with_complaints: 23,
 *       overdue_ppm_count: 5,
 *       overdue_calibration_count: 3,
 *       overdue_compliance_count: 2
 *     }
 *   }
 *
 * @throws {Error} If request fails or token is invalid
 */
export const fetchFilteredDevices = async (filters = {}, pagination = { page: 1, per_page: 25 }, token, sorting = null) => {
  try {
    // Validate inputs
    if (!token) {
      throw new Error('Authentication token is required');
    }

    if (!filters || typeof filters !== 'object') {
      throw new Error('Invalid filters object');
    }

    // Normalize filter object - remove undefined/null values where appropriate
    const normalizedFilters = normalizeFilters(filters);

    // Build request body
    const requestBody = {
      pagination: {
        page: Math.max(1, pagination.page || 1),
        per_page: Math.min(Math.max(1, pagination.per_page || 25), 100), // Min 1, Max 100
      },
      filters: normalizedFilters,
    };

    // Add sorting parameters if provided
    if (sorting && sorting.sort_by) {
      requestBody.sorting = {
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction === 'desc' ? 'desc' : 'asc',
      };
    }

    // Make API call
    const response = await axios.post(
      API_ENDPOINTS.DEVICES.FILTER,
      requestBody,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 second timeout
      }
    );

    // Validate response structure
    if (!response.data || !response.data.data) {
      throw new Error('Invalid response structure from server');
    }

    const { success, data, error } = response.data;

    if (!success) {
      throw new Error(error || 'Failed to fetch filtered devices');
    }

    // Return normalized response
    return {
      success: true,
      devices: data.devices || [],
      pagination: data.pagination || {
        page: 1,
        per_page: 25,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_previous: false,
      },
      summary: data.summary || {
        total_with_complaints: 0,
        overdue_ppm_count: 0,
        overdue_calibration_count: 0,
        overdue_compliance_count: 0,
      },
    };
  } catch (error) {
    // Handle different error types
    let errorMessage = 'Failed to fetch filtered devices';
    let errorCode = 'UNKNOWN_ERROR';

    if (error.response) {
      // Backend returned an error response
      errorCode = error.response.status;

      if (error.response.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        errorCode = 'UNAUTHORIZED';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to view devices.';
        errorCode = 'FORBIDDEN';
      } else if (error.response.status === 400) {
        errorMessage = error.response.data?.error || 'Invalid filter parameters';
        errorCode = 'BAD_REQUEST';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
        errorCode = 'SERVER_ERROR';
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'Network error. Please check your connection.';
      errorCode = 'NETWORK_ERROR';
    } else if (error.message) {
      // Error in request setup
      errorMessage = error.message;
      errorCode = 'REQUEST_ERROR';
    }

    // Throw error with structured info
    const structuredError = new Error(errorMessage);
    structuredError.code = errorCode;
    structuredError.status = error.response?.status;
    structuredError.originalError = error;

    console.error('[filterApi.js] Error fetching filtered devices:', {
      message: errorMessage,
      code: errorCode,
      status: error.response?.status,
      url: error.config?.url,
      originalError: error.message,
    });

    throw structuredError;
  }
};

/**
 * Normalize filter object by removing empty/null values
 * Converts string booleans ('Yes'/'No') where needed
 *
 * @param {Object} filters - Raw filter object from UI
 * @returns {Object} Normalized filter object
 */
export function normalizeFilters(filters) {
  const normalized = {};

  // Copy filter properties, removing empty arrays and null values
  Object.keys(filters).forEach((key) => {
    const value = filters[key];

    // Skip null values
    if (value === null || value === undefined) {
      normalized[key] = value; // Explicitly pass null for optional filters
      return;
    }

    // Handle arrays - only include non-empty
    if (Array.isArray(value)) {
      normalized[key] = value.length > 0 ? value : [];
      return;
    }

    // Handle empty strings - keep as empty string for search field, null for others
    if (typeof value === 'string' && value.trim() === '') {
      // Search field should be empty string, not null
      normalized[key] = key === 'search' ? '' : null;
      return;
    }

    // Include all other values
    normalized[key] = value;
  });

  return normalized;
}

/**
 * Clear all filters - returns default filter state
 *
 * @returns {Object} Default filter object
 */
export const getDefaultFilters = () => ({
  locations: [],
  departments: [],
  serviceContractTypes: [],
  deviceStatus: [],
  complianceRequired: null,
  calibrationRequired: null,
  productCostFrom: null,
  productCostTo: null,
  ppmStatus: [],
  calibrationStatus: [],
  complianceStatus: [],
  coverageStatus: [],  // CONSOLIDATED: applies to both warranty and contract status
  hasActiveComplaints: false,
  hasNoActiveComplaints: false,
  impactsAvailability: '',
  installationDateFrom: null,
  installationDateTo: null,
  warrantyDateFrom: null,
  warrantyDateTo: null,
  contractDateFrom: null,
  contractDateTo: null,
  search: '',
});

/**
 * Check if any filters are currently active
 *
 * @param {Object} filters - Filter object to check
 * @returns {boolean} True if any non-default filters are set
 */
export const hasActiveFilters = (filters) => {
  const defaults = getDefaultFilters();

  return Object.keys(filters).some((key) => {
    // Free-text search is not an Active Filter (cleared via search box only)
    if (key === 'search') return false;

    const filterValue = filters[key];
    const defaultValue = defaults[key];

    if (Array.isArray(filterValue)) {
      return filterValue.length > 0;
    }

    return filterValue !== defaultValue;
  });
};

/**
 * Get user-friendly filter label for display
 *
 * @param {string} filterKey - Filter key name
 * @param {*} filterValue - Filter value
 * @returns {string} User-friendly label
 */
export const getFilterLabel = (filterKey, filterValue) => {
  const labelMap = {
    locations: 'Location',
    departments: 'Department',
    serviceContractTypes: 'Service Contract Type',
    deviceStatus: 'Device Status',
    complianceRequired: 'Compliance Required',
    calibrationRequired: 'Calibration Required',
    productCostFrom: 'Min Product Cost',
    productCostTo: 'Max Product Cost',
    ppmStatus: 'PPM Status',
    calibrationStatus: 'Calibration Status',
    complianceStatus: 'Compliance Status',
    coverageStatus: 'Coverage Status (Warranty/Contract)',
    warrantyStatus: 'Warranty Status (Deprecated)',
    contractStatus: 'Contract Status (Deprecated)',
    hasActiveComplaints: 'Has Active Complaints',
    impactsAvailability: 'Availability Impact',
    hasNoActiveComplaints: 'Has No Active Complaints',
    installationDateFrom: 'Installation Date (From)',
    installationDateTo: 'Installation Date (To)',
    warrantyDateFrom: 'Warranty Date (From)',
    warrantyDateTo: 'Warranty Date (To)',
    contractDateFrom: 'Contract Date (From)',
    contractDateTo: 'Contract Date (To)',
    search: 'Search',
  };

  return labelMap[filterKey] || filterKey;
};

/**
 * Format pagination for API call
 * Ensures valid page/per_page values
 *
 * @param {number} page - Current page (1-based)
 * @param {number} perPage - Items per page
 * @returns {Object} Validated pagination object
 */
export const formatPaginationForApi = (page = 1, perPage = 25) => ({
  page: Math.max(1, parseInt(page, 10) || 1),
  per_page: Math.min(Math.max(1, parseInt(perPage, 10) || 25), 100),
});

/**
 * Fetch IDs for every device matching the current filters (single lightweight request).
 * Sorting is accepted for API compatibility with call sites but ignored — IDs are unordered.
 *
 * @param {Object} filters - Same filter shape as fetchFilteredDevices
 * @param {string} token - JWT authentication token
 * @param {Object|null} sorting - Unused; kept for callers that pass sorting
 * @returns {Promise<number[]>} Matching device IDs
 */
export const fetchAllFilteredDeviceIds = async (filters = {}, token, sorting = null) => {
  void sorting;

  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }

    if (!filters || typeof filters !== 'object') {
      throw new Error('Invalid filters object');
    }

    const normalizedFilters = normalizeFilters(filters);

    const response = await axios.post(
      API_ENDPOINTS.DEVICES.FILTER_IDS,
      { filters: normalizedFilters },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 30000,
      }
    );

    if (!response.data || !response.data.data) {
      throw new Error('Invalid response structure from server');
    }

    const { success, data, error } = response.data;

    if (!success) {
      throw new Error(error || 'Failed to fetch filtered device IDs');
    }

    return data.ids || [];
  } catch (error) {
    let errorMessage = 'Failed to fetch filtered device IDs';
    let errorCode = 'UNKNOWN_ERROR';

    if (error.response) {
      errorCode = error.response.status;

      if (error.response.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        errorCode = 'UNAUTHORIZED';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to view devices.';
        errorCode = 'FORBIDDEN';
      } else if (error.response.status === 400) {
        errorMessage = error.response.data?.error || 'Invalid filter parameters';
        errorCode = 'BAD_REQUEST';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
        errorCode = 'SERVER_ERROR';
      }
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
      errorCode = 'NETWORK_ERROR';
    } else if (error.message) {
      errorMessage = error.message;
      errorCode = 'REQUEST_ERROR';
    }

    const structuredError = new Error(errorMessage);
    structuredError.code = errorCode;
    structuredError.status = error.response?.status;
    structuredError.originalError = error;

    console.error('[filterApi.js] Error fetching filtered device IDs:', {
      message: errorMessage,
      code: errorCode,
      status: error.response?.status,
      url: error.config?.url,
      originalError: error.message,
    });

    throw structuredError;
  }
};

export default {
  fetchFilteredDevices,
  fetchAllFilteredDeviceIds,
  getDefaultFilters,
  hasActiveFilters,
  getFilterLabel,
  formatPaginationForApi,
};
